import React from 'react';
import { useSynapse } from 'app/lib/synaptik';
import { DiagnoseCondition } from 'app/types/Infermedica';
import { Button } from '@goodrx/matisse';
import { ServiceModel } from '@goodrx/heydoctor-core';
import { useHistory } from 'react-router-dom';
import { onTriageCtaInteraction } from 'app/lib/analytics';
import { TriageBlockConfig } from '../TriageBlock';
import { SenderChatBubble } from '../shared';

type ProtocolizedVisitData = {
  condition: DiagnoseCondition;
  conditions: DiagnoseCondition[];
  service: Pick<ServiceModel, 'code'>;
};

export const code: TriageBlockConfig['code'] = 'protocolizedVisit';

export const Message: TriageBlockConfig<ProtocolizedVisitData>['Message'] = ({
  dataValue: { condition, service },
  sessionId,
}) => {
  const history = useHistory();

  const [firstName, deleteIntendedService, createVisit] = useSynapse(({ user, services, visits }) => [
    user.state.profile?.firstName,
    services.deleteIntendedService,
    visits.create,
  ]);

  const handleVisitCreation = async () => {
    const medicalHistoryTaskEnabled = window.optimizelyClientInstance.isFeatureEnabled('PATIENT_HEALTH_RECORD');

    try {
      const stateSelection = sessionStorage.getItem('stateSelection');
      // @ts-ignore
      const { id } = await createVisit({ code: service.code, state: stateSelection, medicalHistoryTaskEnabled });

      deleteIntendedService();
      onTriageCtaInteraction({ type: 'start-visit', sessionId });
      history.replace(`/visits/${id}`);
    } catch (err) {
      history.replace('/account');
    } finally {
      sessionStorage.removeItem('stateSelection');
      sessionStorage.removeItem('triageCache');
    }
  };

  const isGeneralVisit = service.code === 'General';

  return (
    <>
      {isGeneralVisit ? (
        <>
          <SenderChatBubble>
            Thanks {firstName}! We’re almost ready to connect you with a HeyDoctor healthcare provider.
          </SenderChatBubble>
          <SenderChatBubble>
            Next, our doctors and nurses will need to ask you some questions about your medical history.
          </SenderChatBubble>
          <SenderChatBubble>
            As a reminder, this is not a diagnosis or medical advice and your responses have not yet been seen by a
            healthcare provider. Please seek immediate medical care if your symptoms seem serious.
          </SenderChatBubble>
        </>
      ) : (
        <>
          <SenderChatBubble>
            Thanks {firstName}! People with similar responses were likely to have{' '}
            <strong>{condition?.commonName?.toLowerCase()}</strong>. This is not a diagnosis and has not been reviewed
            by a healthcare provider. Please seek immediate medical care if your symptoms seem serious.
          </SenderChatBubble>

          <SenderChatBubble>
            We’re ready to connect you with a HeyDoctor healthcare provider! Next our doctors and nurses will need to
            ask you a few more questions about your medical history.
          </SenderChatBubble>
        </>
      )}

      <SenderChatBubble>
        When you’re ready, click <strong>Start My Visit</strong> to exit the chat and continue to the intake interview.
      </SenderChatBubble>
      <Button mt={2} variant="primary" onClick={handleVisitCreation} data-qa="start-my-visit-btn">
        Start My Visit
      </Button>
    </>
  );
};

export const Response = () => null;

export const SavedResponse = () => null;
